/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { resolveCoreApiPath } from '../utils/coreAssetUrl';

export type DataTableRowMode = 'ellipsis' | 'scroll' | 'wrap';

export interface DataTableTablePrefs {
  rowMode?: DataTableRowMode;
  hiddenColumns?: string[];
  columnWidths?: Record<string, number>;
  /** Veri sütunları (actions hariç) görünüm sırası — `preferenceId` / `String(key)` ile `hiddenColumns` ve `columnWidths` ile aynı kimlik uzayı. */
  columnOrder?: string[];
}

export interface DataTableUiDocV1 {
  version: 1;
  defaultRowMode: DataTableRowMode;
  tables: Record<string, DataTableTablePrefs>;
}

export const DATA_TABLE_UI_SETTING_KEY = 'dataTableUi';

/** Sunucuya yazılacak boş / varsayılan tablo UI belgesi (sıfırlama ve ilk kurulum). */
export const DATA_TABLE_UI_DEFAULT_DOC: DataTableUiDocV1 = {
  version: 1,
  defaultRowMode: 'ellipsis',
  tables: {},
};

/** Sunucudan veya localStorage’dan gelen ham değeri birleştirir (UserSettings vb. için dışa açık). */
export function mergeDataTableUiDoc(raw: unknown): DataTableUiDocV1 {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ...DATA_TABLE_UI_DEFAULT_DOC, tables: { ...DATA_TABLE_UI_DEFAULT_DOC.tables } };
  }
  const o = raw as Record<string, unknown>;
  const defaultRowMode: DataTableRowMode =
    o.defaultRowMode === 'scroll' || o.defaultRowMode === 'wrap' || o.defaultRowMode === 'ellipsis'
      ? o.defaultRowMode
      : 'ellipsis';
  const tablesRaw = o.tables;
  const tables: Record<string, DataTableTablePrefs> =
    typeof tablesRaw === 'object' && tablesRaw !== null && !Array.isArray(tablesRaw)
      ? { ...(tablesRaw as Record<string, DataTableTablePrefs>) }
      : {};
  return { version: 1, defaultRowMode, tables };
}

function cloneTablePrefsSlice(p: DataTableTablePrefs): DataTableTablePrefs {
  return {
    ...p,
    columnOrder: p.columnOrder ? [...p.columnOrder] : undefined,
    hiddenColumns: p.hiddenColumns ? [...p.hiddenColumns] : undefined,
    columnWidths: p.columnWidths ? { ...p.columnWidths } : undefined,
  };
}

function readTablePrefsEntry(
  tables: Record<string, DataTableTablePrefs>,
  preferencesKey: string,
): DataTableTablePrefs | undefined {
  const k = preferencesKey.trim();
  if (!k || !Object.prototype.hasOwnProperty.call(tables, k)) return undefined;
  const prefs = tables[k];
  if (!prefs || typeof prefs !== 'object') return undefined;
  return cloneTablePrefsSlice(prefs);
}

/** Bu tablo kimliğine ait `tables` girdisini kaldırır. */
export function stripTablePrefsKeysForKey(
  tables: Record<string, DataTableTablePrefs>,
  preferencesKey: string,
): Record<string, DataTableTablePrefs> {
  const k = preferencesKey.trim();
  if (!k) return { ...tables };
  const next: Record<string, DataTableTablePrefs> = { ...tables };
  delete next[k];
  return next;
}

function tableHasStoredPrefsInDoc(
  doc: DataTableUiDocV1,
  preferencesKey: string,
): boolean {
  const k = preferencesKey.trim();
  if (!k) return false;
  return Object.prototype.hasOwnProperty.call(doc.tables, k);
}

const DATA_TABLE_UI_LOAD_CACHE_MS = 30000;
let dataTableUiInflight: Promise<DataTableUiDocV1> | null = null;
let dataTableUiCache: DataTableUiDocV1 | null = null;
let dataTableUiCacheAt = 0;

function cloneDataTableUiDoc(doc: DataTableUiDocV1): DataTableUiDocV1 {
  return {
    ...doc,
    tables: Object.fromEntries(
      Object.entries(doc.tables || {}).map(([k, v]) => [k, cloneTablePrefsSlice(v || {})]),
    ),
  };
}

function setDataTableUiCache(doc: DataTableUiDocV1): void {
  dataTableUiCache = cloneDataTableUiDoc(doc);
  dataTableUiCacheAt = Date.now();
}

function isAuthFailure(error: unknown): boolean {
  const maybeError = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
  const rawStatus = maybeError?.response?.status ?? maybeError?.status;
  const status = Number(rawStatus);
  return status === 401 || status === 403;
}

function persistDataTableUiDoc(doc: DataTableUiDocV1): void {
  void axios
    .put(resolveCoreApiPath(`/api/user-settings/${DATA_TABLE_UI_SETTING_KEY}`), { value: doc })
    .catch(() => {});
}

function loadDataTableUiDoc(): Promise<DataTableUiDocV1> {
  const now = Date.now();
  if (dataTableUiCache && now - dataTableUiCacheAt < DATA_TABLE_UI_LOAD_CACHE_MS) {
    return Promise.resolve(cloneDataTableUiDoc(dataTableUiCache));
  }
  if (dataTableUiInflight) {
    return dataTableUiInflight.then(cloneDataTableUiDoc);
  }

  const dataTableUiUrl = resolveCoreApiPath(`/api/user-settings/${DATA_TABLE_UI_SETTING_KEY}`);
  dataTableUiInflight = axios
    .get(dataTableUiUrl)
    .then((res) => {
      const v = res.data?.data?.value ?? res.data?.value ?? res.data?.data;
      const doc = mergeDataTableUiDoc(v);
      setDataTableUiCache(doc);
      return cloneDataTableUiDoc(doc);
    })
    .catch((error) => {
      const doc = { ...DATA_TABLE_UI_DEFAULT_DOC, tables: { ...DATA_TABLE_UI_DEFAULT_DOC.tables } };
      if (!isAuthFailure(error)) {
        setDataTableUiCache(doc);
      }
      return cloneDataTableUiDoc(doc);
    })
    .finally(() => {
      dataTableUiInflight = null;
    });

  return dataTableUiInflight.then(cloneDataTableUiDoc);
}

function applyTablePrefsUpdate(
  prev: DataTableUiDocV1,
  preferencesKey: string,
  updater: (cur: DataTableTablePrefs) => DataTableTablePrefs,
): DataTableUiDocV1 {
  const cur = readTablePrefsEntry(prev.tables, preferencesKey) || {};
  const updated = updater(cur);
  const tables: Record<string, DataTableTablePrefs> = {
    ...stripTablePrefsKeysForKey(prev.tables, preferencesKey),
    [preferencesKey]: updated,
  };
  return { ...prev, tables };
}

export interface UseDataTableUiOptions {
  /** `dataTableStorageTableId('listeId')` önerilir — ürün kodu önekli; bkz. GLOBAL_SYNC_SYSTEM §2.4 */
  preferencesKey?: string;
  /** preferencesKey yok veya ayarlar henüz yüklenmediyse */
  fallbackRowMode?: DataTableRowMode;
}

export function useDataTableUi(options: UseDataTableUiOptions) {
  const { preferencesKey, fallbackRowMode = 'ellipsis' } = options;
  const key = preferencesKey?.trim() || undefined;
  const { isInitialized, isAuthenticated } = useAuth();
  const [doc, setDoc] = useState<DataTableUiDocV1>(() => ({ ...DATA_TABLE_UI_DEFAULT_DOC, tables: { ...DATA_TABLE_UI_DEFAULT_DOC.tables } }));
  const [loaded, setLoaded] = useState(!key);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!key) {
      setLoaded(true);
      return;
    }
    if (!isInitialized || !isAuthenticated) {
      setLoaded(true);
      return;
    }

    let cancelled = false;
    setLoaded(false);

    void loadDataTableUiDoc()
      .then((v) => {
        if (cancelled) return;
        const nextDoc = cloneDataTableUiDoc(dataTableUiCache || v);
        setDoc(nextDoc);
      })
      .catch(() => {
        if (!cancelled) setDoc({ ...DATA_TABLE_UI_DEFAULT_DOC, tables: { ...DATA_TABLE_UI_DEFAULT_DOC.tables } });
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [key, isInitialized, isAuthenticated]);

  const scheduleSave = useCallback(
    (nextDoc: DataTableUiDocV1) => {
      if (!key || !isAuthenticated) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      setDataTableUiCache(nextDoc);
      saveTimerRef.current = setTimeout(() => {
        persistDataTableUiDoc(nextDoc);
      }, 450);
    },
    [key, isAuthenticated],
  );

  const persistDocNow = useCallback(
    (nextDoc: DataTableUiDocV1) => {
      if (!key || !isAuthenticated) return;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setDataTableUiCache(nextDoc);
      persistDataTableUiDoc(nextDoc);
    },
    [key, isAuthenticated],
  );

  const clearTablePrefs = useCallback(() => {
    if (!key || !isAuthenticated) return;
    let nextDoc: DataTableUiDocV1 | undefined;
    setDoc((prev) => {
      nextDoc = { ...prev, tables: stripTablePrefsKeysForKey(prev.tables, key) };
      return nextDoc;
    });
    if (nextDoc) persistDocNow(nextDoc);
  }, [key, isAuthenticated, persistDocNow]);

  const setTableRowMode = useCallback(
    (mode: DataTableRowMode) => {
      if (!key) return;
      setDoc((prev) => {
        const next = applyTablePrefsUpdate(prev, key, (c) => ({ ...c, rowMode: mode }));
        scheduleSave(next);
        return next;
      });
    },
    [key, scheduleSave],
  );

  const setDefaultRowMode = useCallback(
    (mode: DataTableRowMode) => {
      setDoc((prev) => {
        const next: DataTableUiDocV1 = { ...prev, defaultRowMode: mode };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const setHiddenColumns = useCallback(
    (hidden: string[]) => {
      if (!key) return;
      setDoc((prev) => {
        const next = applyTablePrefsUpdate(prev, key, (c) => ({ ...c, hiddenColumns: hidden }));
        scheduleSave(next);
        return next;
      });
    },
    [key, scheduleSave],
  );

  const patchColumnWidths = useCallback(
    (patch: Record<string, number>) => {
      if (!key) return;
      setDoc((prev) => {
        const next = applyTablePrefsUpdate(prev, key, (c) => ({
          ...c,
          columnWidths: { ...(c.columnWidths || {}), ...patch },
        }));
        scheduleSave(next);
        return next;
      });
    },
    [key, scheduleSave],
  );

  const setColumnOrder = useCallback(
    (orderedIds: string[]) => {
      if (!key) return;
      setDoc((prev) => {
        const next = applyTablePrefsUpdate(prev, key, (c) => ({ ...c, columnOrder: [...orderedIds] }));
        scheduleSave(next);
        return next;
      });
    },
    [key, scheduleSave],
  );

  const effectiveRowMode: DataTableRowMode = useMemo(() => {
    if (!key || !loaded) return fallbackRowMode;
    const t = readTablePrefsEntry(doc.tables, key);
    return t?.rowMode ?? doc.defaultRowMode;
  }, [key, loaded, doc, fallbackRowMode]);

  const tablePrefs = key ? readTablePrefsEntry(doc.tables, key) : undefined;

  const hasStoredTablePrefs = useMemo(
    () => Boolean(key && tableHasStoredPrefsInDoc(doc, key)),
    [key, doc],
  );

  return {
    loaded,
    doc,
    tablePrefs,
    effectiveRowMode,
    hasStoredTablePrefs,
    clearTablePrefs,
    setTableRowMode,
    setDefaultRowMode,
    setHiddenColumns,
    patchColumnWidths,
    setColumnOrder,
    defaultRowMode: doc.defaultRowMode,
  };
}

/** Uydu / sayfa: `preferencesKey={dataTableStorageTableId('companies')}` — tek import noktası. */
export { dataTableStorageTableId } from '../config/app.config';
